import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import React from 'react';
import { CircularProgress } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableFooter from '@material-ui/core/TableFooter';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import styles from '../../assets/jss/components/tableStyle';
import GridItem from '../Grid/GridItem';
import Typography from '../Typography/CustomTypography';
import { hexToRgbBlur } from '../../assets/jss/global';

const useStyles = makeStyles(styles);

export const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: (props) => props.cellColor || '#2FC8FE',
        color: '#fff',
        fontWeight: 700,
        borderRight: (props) => (props.borderColor && `1px solid ${props.borderColor}`) || '1px solid #FFFFFF',
        borderBottom: (props) => (props.borderColor && `1px solid ${props.borderColor}`),
        borderTop: (props) => (props.borderColor && `1px solid ${props.borderColor}`),
        [theme.breakpoints.down('xs')]: {
            fontSize: '80%',
        },
        '&:first-child': {
            borderLeft: (props) => (props.borderColor && `1px solid ${props.borderColor}`),
        },
    },
    body: {
        color: (props) => props.cellTextColorBody || '#4B4B4B',
        fontSize: 14,
        borderRight: (props) => (props.borderColor && `1px solid ${props.borderColor}`) || '1px solid #FFFFFF',
        borderBottom: (props) => (props.borderColor && `1px solid ${props.borderColor}`),
        maxWidth: '500px',
        minWidth: 110,
        wordBreak: 'break-all',
        [theme.breakpoints.down('xs')]: {
            fontSize: '80%',
            minWidth: 100,
        },
        '&:first-child': {
            borderLeft: (props) => (props.borderColor && `1px solid ${props.borderColor}`),
        },
    },
    footer: {
        borderRight: (props) => (props.borderColor && `1px solid ${props.borderColor}`),
        borderBottom: (props) => (props.borderColor && `1px solid ${props.borderColor}`),
        borderLeft: (props) => (props.borderColor && `1px solid ${props.borderColor}`),
    },
}))(TableCell);

export const StyledTableRow = withStyles(() => ({
    root: {
        cursor: (props) => ((props.hover && !props.pointerLess) ? 'pointer' : 'auto'),
        position: 'relative',
        '&:nth-of-type(odd)': {
            backgroundColor: (props) => props?.cellBackgroundColor?.odd_record_background_color || (props.borderColor ? '#fff' : 'rgba(231, 233, 236, 0.5)'),
        },
        '&:nth-of-type(even)': {
            backgroundColor: (props) => props?.cellBackgroundColor?.even_record_background_color || (props.borderColor ? '#fff' : '#E7E9EC'),
        },
        '&:nth-of-type(odd):hover, &:nth-of-type(odd):focus': {
            backgroundColor: (props) => (props.hover
                ? ((props?.cellBackgroundColor?.odd_record_background_color && `rgba(${hexToRgbBlur(props?.cellBackgroundColor?.odd_record_background_color, 0.75)})`)
                || '#b4d3de')
                : (props?.cellBackgroundColor?.odd_record_background_color || 'rgba(231, 233, 236, 0.5)')),
        },
        '&:nth-of-type(even):hover, &:nth-of-type(even):focus': {
            backgroundColor: (props) => (props.hover
                ? ((props?.cellBackgroundColor?.even_record_background_color && `rgba(${hexToRgbBlur(props?.cellBackgroundColor?.even_record_background_color, 0.75)})`)
                || '#b4d3de')
                : (props?.cellBackgroundColor?.even_record_background_color || '#E7E9EC')),
        },
    },
}))(TableRow);

const CustomTable = (props) => {
    const classes = useStyles();
    const {
        tableHead,
        tableData,
        tableHeadWidth,
        tableHeaderColor,
        tableFooter,
        onClick,
        isLoading,
        noDataMessage,
        className,
        cellColor,
        variantHeader,
        hover,
        pointerLess,
        cellTextColorBody,
        cellTextColorHear,
        cellBackgroundColor,
        borderColor,
    } = props;

    const handleClickRow = (index) => {
        if (onClick) {
            onClick(index);
        }
    };

    const renderTableRows = () => {
        if (isLoading) {
            return (
                <StyledTableRow>
                    <StyledTableCell colSpan={tableHead.length} align="center">
                        <CircularProgress size={25} classes={{ colorPrimary: classes.spinner }} />
                    </StyledTableCell>
                </StyledTableRow>
            );
        }
        if (!tableData.length) {
            return (
                <StyledTableRow cellBackgroundColor={cellBackgroundColor} borderColor={borderColor}>
                    <StyledTableCell colSpan={tableHead.length} align="center" cellTextColorBody={cellTextColorBody} borderColor={borderColor}>
                        {noDataMessage}
                    </StyledTableCell>
                </StyledTableRow>
            );
        }
        return tableData.map((prop, index) => (
            <StyledTableRow
                onClick={() => handleClickRow(index)}
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                hover={hover}
                pointerLess={pointerLess}
                cellBackgroundColor={cellBackgroundColor}
                borderColor={borderColor}
            >
                {prop.map((item, propIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledTableCell key={propIndex} cellTextColorBody={cellTextColorBody} borderColor={borderColor}>
                        {item}
                    </StyledTableCell>
                ))}
            </StyledTableRow>
        ));
    };

    return (
        <div className={classes.table_main}>
            <TableContainer component={Paper} className={className}>
                <Table className={classes.table} stickyHeader aria-label="sticky table">
                    {tableHead !== undefined ? (
                        <TableHead className={classes[`${tableHeaderColor}TableHeader`]}>
                            <StyledTableRow>
                                {tableHead.map((prop, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <StyledTableCell cellColor={cellColor} key={`${prop}_${index}`} style={{ width: tableHeadWidth[index] }} borderColor={borderColor}>
                                        <Typography variant={variantHeader} color="inherit" bold customColor={cellTextColorHear}>
                                            {prop}
                                        </Typography>
                                    </StyledTableCell>
                                ))}
                            </StyledTableRow>
                        </TableHead>
                    ) : null}
                    <TableBody>
                        {renderTableRows()}
                    </TableBody>
                    {
                        !!tableData.length && tableFooter && (
                            <TableFooter>
                                <StyledTableCell
                                    style={{ backgroundColor: cellBackgroundColor?.even_record_background_color || 'rgba(231, 233, 236, 0.5)' }}
                                    align="center"
                                    colSpan={tableHead ? tableHead.length : 10}
                                    borderColor={borderColor}
                                >
                                    {tableFooter}
                                </StyledTableCell>
                            </TableFooter>
                        )
                    }
                </Table>
            </TableContainer>
        </div>
    );
};

CustomTable.defaultProps = {
    tableHeaderColor: 'primary',
    tableHead: [],
    tableData: [],
    className: '',
    tableHeadWidth: [],
    tableFooter: undefined,
    isLoading: false,
    noDataMessage: (
        <GridItem
            xs={12}
            style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '20px',
                zIndex: 1,
            }}
        >
            <FormattedMessage id="noData" />
        </GridItem>),
    onClick: undefined,
    cellColor: '',
    variantHeader: 'subtitle2',
    hover: true,
    pointerLess: false,
    cellTextColorBody: '',
    cellTextColorHear: '',
    cellBackgroundColor: {},
    borderColor: '',
};

CustomTable.propTypes = {
    tableHeaderColor: PropTypes.oneOf([
        'warning',
        'primary',
        'danger',
        'success',
        'info',
        'rose',
        'gray',
    ]),
    tableHead: PropTypes.arrayOf(PropTypes.node),
    tableData: PropTypes.arrayOf(PropTypes.array),
    tableHeadWidth: PropTypes.arrayOf(PropTypes.string),
    className: PropTypes.oneOfType([() => null, PropTypes.object]),
    tableFooter: PropTypes.element,
    onClick: PropTypes.func,
    isLoading: PropTypes.bool,
    noDataMessage: PropTypes.node,
    cellColor: PropTypes.string,
    cellTextColorBody: PropTypes.string,
    cellTextColorHear: PropTypes.string,
    cellBackgroundColor: PropTypes.oneOfType([() => null, PropTypes.object]),
    variantHeader: PropTypes.oneOf([
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'subtitle1',
        'subtitle2',
        'body1',
        'body2',
        'caption',
        'button',
        'overline',
        'srOnly',
        'inherit',
    ]),
    hover: PropTypes.bool,
    pointerLess: PropTypes.bool,
    borderColor: PropTypes.string,
};

export default CustomTable;
